import {DigitizationTools} from "./ToolRegistry.js";
import {FeatureVisual} from "./FeatureVisual.js";
import {createFeature} from "./Feature.js";

import {T} from "./i18n/index.js";
// Snitplaner (viewer.snitplan, modules/Snitplan/) som objekter i Registreringer/Målinger-træet - ÉN række pr. Snitplan.
// Samme mønster som nativeClip.js, men Snitplanet er et fælles objekt: kun denne bro spejler det (nativeClip.js springer
// Snitplan-volumener over via object.pvSnit, så de ikke også får en "Clip Volume N"-række).
//
//   træets øje      ->  volume.visible (et skjult Snitplan har ingen clip-effekt - se SnitplanManager._apply)
//   træets slet     ->  viewer.snitplan.remove (Potree-clip OG IFC-effekten forsvinder med objektet)
//   Snitplan fjernet ->  featuren fjernes (fx ESC under oprettelse, Delete-tasten, "fjern alle")
//   omdøbning       <->  navnet (træ <-> popup) er ét og samme felt
//   valgt i træet   ->  volumen markeres i vieweren (Potrees egen selection; ingen handles for et Snitplan)

export const SNIT_TOOL_ID = "digitize_snit";

export const SnitTool = {
	id: SNIT_TOOL_ID,
	name: T("Snit"),
	category: "hidden",
	kind: "measurement",
	icon: "clip_volume.svg",
	resultGeometryType: "Point",
	featureType: T("Snit"),
	tooltip: T("Snit (skærende boks i punktskyer og IFC)."),
	customCapture(){
		// Intet: Snitplaner oprettes af viewer.snitplan (placering i 3D), ikke af et tegneværktøj.
	},
};

DigitizationTools.register(SnitTool);

// Tegner intet selv (Snitplanet har sin egen ramme) - bærer kun træ-rækkens tilstand. Geometrien (rækkens "position") læses
// levende fra volumenen, så den følger Snitplanets centrum.
class NativeSnitVisual extends FeatureVisual{
	constructor(viewer, volume){
		super("Point");
		this.editable = false;
		this._viewer = viewer;
		this._volume = volume;
		this.userData.nativeSnit = {volume};
	}

	rawCoordinates(){
		const p = this._volume.position;
		return [[p.x, p.y, p.z]];
	}

	toCoordinates(){
		return this.rawCoordinates()[0];
	}

	setSelected(value){
		super.setSelected(value);
		const input = this._viewer.inputHandler;
		if(!input) return;
		const selected = input.selection.indexOf(this._volume) !== -1;
		if(value){
			if(!selected){
				input.deselectAll();
				input.toggleSelection(this._volume);
			}
		}else if(selected){
			input.deselectAll();
		}
	}
}

// Spejler et Snitplan som en feature. Idempotent pr. volumen. Returnerer feature-id'et.
export function registerNativeSnit(dm, volume){
	if(!volume || !volume.pvSnit) return null;

	for(const [featureId, existing] of dm.visuals){
		if(existing.userData.nativeSnit && existing.userData.nativeSnit.volume === volume) return featureId;
	}

	const manager = dm.viewer.snitplan;
	const visual = new NativeSnitVisual(dm.viewer, volume);
	dm.scene.add(visual);

	const feature = createFeature({
		geometryType: "Point",
		coordinates: visual.toCoordinates(),
		category: SnitTool.category,
		tool: SNIT_TOOL_ID,
		featureType: SnitTool.featureType,
		name: volume.snit.name,
		attributes: {snitId: volume.snit.id},
	});
	visual.setInfo(feature.properties.name, feature.properties.description);

	// Træets øje styrer Snitplanets egen synlighed.
	Object.defineProperty(visual, "visible", {
		configurable: true,
		get: () => volume.visible !== false,
		set: (value) => { volume.visible = !!value; },
	});

	dm._wireFeatureLifecycle(feature, visual);
	// et Snitplan-center er ikke et snap-mål
	dm.removeFeatureSnapTargets(feature.id);

	let released = false;
	const release = () => {
		if(released) return false;
		released = true;
		manager.removeEventListener("snit_removed", onSnitRemoved);
		volume.removeEventListener("snit_changed", onSnitChanged);
		dm.featureManager.removeEventListener("feature_removed", onFeatureRemoved);
		dm.featureManager.removeEventListener("feature_changed", onFeatureChanged);
		return true;
	};
	const onSnitRemoved = (e) => {
		if(e.snit !== volume) return;
		if(!release()) return;
		if(dm.getVisual(feature.id)) dm.removeFeature(feature.id);
	};
	const onFeatureRemoved = (e) => {
		if(!e.feature || e.feature.id !== feature.id) return;
		if(!release()) return;
		const input = dm.viewer.inputHandler;
		if(input && input.selection.indexOf(volume) !== -1) input.deselectAll();
		manager.remove(volume);
	};
	// navnet er ét felt: popup -> træ ...
	const onSnitChanged = () => {
		const name = volume.snit.name;
		if(name && feature.properties.name !== name) dm.renameFeature(feature.id, name);
	};
	// ... og træ -> popup/definition
	const onFeatureChanged = (e) => {
		if(!e.feature || e.feature.id !== feature.id) return;
		const name = feature.properties.name;
		if(name && volume.snit.name !== name) volume.setDefinition({name});
	};
	manager.addEventListener("snit_removed", onSnitRemoved);
	volume.addEventListener("snit_changed", onSnitChanged);
	dm.featureManager.addEventListener("feature_removed", onFeatureRemoved);
	dm.featureManager.addEventListener("feature_changed", onFeatureChanged);

	dm.dispatchEvent({type: "feature_created", feature, visual});

	return feature.id;
}

// {volume} bag en feature (null for alle andre registreringer).
export function getNativeSnit(dm, featureId){
	const visual = dm.getVisual(featureId);
	return visual && visual.userData.nativeSnit ? visual.userData.nativeSnit : null;
}
